import Auth from '@aws-amplify/auth'

// A live Cognito access token for outbound API calls.
//
// The problem this solves: every authenticated request used to read
// `store.state.auth.currentUser.accessToken.jwtToken`, a value written to Vuex
// (and localStorage) only by logIn and by validate(). validate() is the refresh
// path, Auth.currentAuthenticatedUser() calls getSession() internally, which
// spends the refresh token when the access token has expired, but it runs in
// just two places: app init, and the router's beforeEach guard. So the token was
// refreshed on NAVIGATION and nowhere else. Sit on one page past the access
// token's lifetime (Cognito's default is 60 minutes) and every request kept
// carrying the same dead JWT until something changed route.
//
// Auth.currentSession() checks expiry locally and hands back the cached token
// when it is still valid, so this is not a network round trip per request; it
// reaches Cognito only when a refresh is actually due.
//
// `fallback` is the caller's stored token, used when Cognito cannot be reached.
// Pass `store.state.auth.currentUser?.accessToken?.jwtToken`.
export async function accessToken(fallback) {
  // Offline: Cognito is unreachable, so a refresh is impossible and awaiting one
  // only stalls the request. Serve the stored token instead. This mirrors the
  // deliberate offline short-circuit in the auth module's validate(): client-side
  // auth is a UX affordance, the server is the real gate, and a stale token gets
  // a clean 401 rather than a hung request. Without this branch the offline
  // degradation the app already supports would turn into blanket 401s.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return fallback
  }

  try {
    const session = await Auth.currentSession()
    return session.getAccessToken().getJwtToken()
  } catch {
    // Two different failures land here and the fallback is right for both. If the
    // refresh token is genuinely dead, the stored token is dead too and the
    // server answers 401 either way. The router guard's validate() is what
    // notices and redirects to login. If instead this was a transient blip while
    // navigator.onLine was true, the stored token may well still be valid, and
    // sending it beats sending nothing.
    return fallback
  }
}

// storedAccessToken reads the token persisted by the auth module. Kept here so
// the several call sites spell the optional chain the same way. It is deep
// enough that one of them getting it subtly wrong is a silent unauthenticated
// request rather than an error.
export function storedAccessToken(store) {
  return store?.state?.auth?.currentUser?.accessToken?.jwtToken
}
